import { Suspense, lazy, useEffect, useState } from 'react';

import { toast } from 'sonner';

import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const Calculator = lazy(() => import('lucide-react').then(mod => ({ default: mod.Calculator })));
const X = lazy(() => import('lucide-react').then(mod => ({ default: mod.X })));

const GUIDE_DISMISSED_KEY = 'factor-tables-guide-dismissed';

const FactorTablesModal = () => {
  const [open, setOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [activeTab, setActiveTab] = useState('future-value');

  useEffect(() => {
    const savedTab = localStorage.getItem('factor-tables-active-tab');
    if (savedTab) {
      setActiveTab(savedTab);
    }
    const guideDismissed = localStorage.getItem(GUIDE_DISMISSED_KEY);
    if (!guideDismissed) {
      setShowGuide(true);
    }
  }, []);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    localStorage.setItem('factor-tables-active-tab', value);
  };

  return (
    <>
      <div
        className="fixed right-4 md:right-6 z-40"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 6rem)' }}
      >
        <Button
          className="h-12 shadow-lg hover:shadow-xl transition-all duration-300 bg-blue-600 hover:bg-blue-700 rounded-full px-4 gap-2 flex items-center"
          onClick={() => setOpen(true)}
        >
          <Suspense fallback={<div className="h-5 w-5" />}>
            <Calculator className="h-5 w-5 shrink-0" />
          </Suspense>
          <span className="text-sm font-medium whitespace-nowrap">현가계수</span>
        </Button>
      </div>

      {showGuide && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-sm mx-4 p-6 text-center">
            <Suspense fallback={<div className="h-12 w-12 mx-auto mb-4" />}>
              <Calculator className="h-12 w-12 mx-auto mb-4 text-blue-600" />
            </Suspense>
            <p className="text-gray-700 mb-6 leading-relaxed">
              <strong>현재&미래가치 Table</strong>은 화면 오른쪽 아래
              아이콘을 클릭하면 볼 수 있습니다.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => setShowGuide(false)}
              >
                닫기
              </Button>
              <Button
                onClick={() => {
                  localStorage.setItem(GUIDE_DISMISSED_KEY, 'true');
                  setShowGuide(false);
                }}
              >
                다시 보지 않기
              </Button>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setOpen(false)} />

          <div className="relative bg-white rounded-lg shadow-xl w-[90vw] max-h-[90vh] m-4 overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold">현가계수</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
                className="h-8 w-8 p-0"
              >
                <Suspense fallback={<div className="w-4 h-4" />}>
                  <X className="w-4 h-4" />
                </Suspense>
              </Button>
            </div>

            <div className="p-6 overflow-auto max-h-[80vh]">
              <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-6">
                  <TabsTrigger value="future-value">W1 미래가치</TabsTrigger>
                  <TabsTrigger value="present-value">W1 현재가치</TabsTrigger>
                  <TabsTrigger value="annuity-future">정상연금 미래가치</TabsTrigger>
                  <TabsTrigger value="annuity-present">정상연금 현재가치</TabsTrigger>
                </TabsList>

                <TabsContent value="future-value">
                  <FutureValueTable />
                </TabsContent>
                <TabsContent value="present-value">
                  <PresentValueTable />
                </TabsContent>
                <TabsContent value="annuity-future">
                  <AnnuityFutureValueTable />
                </TabsContent>
                <TabsContent value="annuity-present">
                  <AnnuityPresentValueTable />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const copyToClipboard = async (value: number) => {
  try {
    await navigator.clipboard.writeText(value.toFixed(5));
    toast.success(`복사완료: ${value.toFixed(5)}`);
  } catch (err) {
    console.error('복사 실패:', err);
    toast.error('복사 실패');
  }
};

const FutureValueTable = () => {
  const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null);
  const data = [
    [
      1.01, 1.02, 1.03, 1.04, 1.05, 1.06, 1.07, 1.08, 1.09, 1.1, 1.11, 1.12, 1.13, 1.14, 1.15, 1.16,
      1.17, 1.18, 1.19, 1.2,
    ],
    [
      1.0201, 1.0404, 1.0609, 1.0816, 1.1025, 1.1236, 1.1449, 1.1664, 1.1881, 1.21, 1.2321, 1.2544,
      1.2769, 1.2996, 1.3225, 1.3456, 1.3689, 1.3924, 1.4161, 1.44,
    ],
    [
      1.0303, 1.06121, 1.09273, 1.12486, 1.15763, 1.19102, 1.22504, 1.25971, 1.29503, 1.331,
      1.36763, 1.40493, 1.4429, 1.48154, 1.52088, 1.5609, 1.60161, 1.64303, 1.68516, 1.728,
    ],
    [
      1.0406, 1.08243, 1.12551, 1.16986, 1.21551, 1.26248, 1.3108, 1.36049, 1.41158, 1.4641,
      1.51807, 1.57352, 1.63047, 1.68896, 1.74901, 1.81064, 1.87389, 1.93878, 2.00534, 2.0736,
    ],
    [
      1.05101, 1.10408, 1.15927, 1.21665, 1.27628, 1.33823, 1.40255, 1.46933, 1.53862, 1.61051,
      1.68506, 1.76234, 1.84244, 1.92541, 2.01136, 2.10034, 2.19245, 2.28776, 2.38635, 2.48832,
    ],
    [
      1.06152, 1.12616, 1.19405, 1.26532, 1.3401, 1.41852, 1.50073, 1.58687, 1.6771, 1.77156,
      1.87041, 1.97382, 2.08195, 2.19497, 2.31306, 2.4364, 2.56516, 2.69955, 2.83976, 2.98598,
    ],
    [
      1.07214, 1.14869, 1.22987, 1.31593, 1.4071, 1.50363, 1.60578, 1.71382, 1.82804, 1.94872,
      2.07616, 2.21068, 2.35261, 2.50227, 2.66002, 2.82622, 3.00124, 3.18547, 3.37932, 3.58318,
    ],
    [
      1.08286, 1.17166, 1.26677, 1.36857, 1.47746, 1.59385, 1.71819, 1.85093, 1.99256, 2.14359,
      2.30454, 2.47596, 2.65844, 2.85259, 3.05902, 3.27841, 3.51145, 3.75886, 4.02139, 4.29982,
    ],
    [
      1.09369, 1.19509, 1.30477, 1.42331, 1.55133, 1.68948, 1.83846, 1.999, 2.17189, 2.35795,
      2.55804, 2.77308, 3.00404, 3.25195, 3.51788, 3.80296, 4.1084, 4.43545, 4.78545, 5.15978,
    ],
    [
      1.10462, 1.21899, 1.34392, 1.48024, 1.62889, 1.79085, 1.96715, 2.15892, 2.36736, 2.59374,
      2.83942, 3.10585, 3.39457, 3.70722, 4.04556, 4.41144, 4.80683, 5.23384, 5.69468, 6.19174,
    ],
    [
      1.11567, 1.24337, 1.38423, 1.53945, 1.71034, 1.8983, 2.10485, 2.33164, 2.58043, 2.85312,
      3.15176, 3.47855, 3.83586, 4.22623, 4.65239, 5.11726, 5.62399, 6.17593, 6.77667, 7.43008,
    ],
    [
      1.12683, 1.26824, 1.42576, 1.60103, 1.79586, 2.0122, 2.25219, 2.51817, 2.81266, 3.13843,
      3.49845, 3.89598, 4.33452, 4.8179, 5.35025, 5.93603, 6.58007, 7.28759, 8.06424, 8.9161,
    ],
    [
      1.13809, 1.29361, 1.46853, 1.66507, 1.88565, 2.13293, 2.40985, 2.71962, 3.0658, 3.45227,
      3.88328, 4.36349, 4.89801, 5.49241, 6.15279, 6.88579, 7.69868, 8.59936, 9.59645, 10.69932,
    ],
    [
      1.14947, 1.31948, 1.51259, 1.73168, 1.97993, 2.2609, 2.57853, 2.93719, 3.34173, 3.7975,
      4.31044, 4.88711, 5.53475, 6.26135, 7.07571, 7.98752, 9.00745, 10.14724, 11.41977, 12.83918,
    ],
    [
      1.16097, 1.34587, 1.55797, 1.80094, 2.07893, 2.39656, 2.75903, 3.17217, 3.64248, 4.17725,
      4.78459, 5.47357, 6.25427, 7.13794, 8.13706, 9.26552, 10.53872, 11.97375, 13.58953, 15.40702,
    ],
    [
      1.17258, 1.37279, 1.60471, 1.87298, 2.18287, 2.54035, 2.95216, 3.42594, 3.97031, 4.59497,
      5.31089, 6.13039, 7.06733, 8.13725, 9.35762, 10.748, 12.3303, 14.12902, 16.17154, 18.48843,
    ],
    [
      1.1843, 1.40024, 1.65285, 1.9479, 2.29202, 2.69277, 3.15882, 3.70002, 4.32763, 5.05447,
      5.89509, 6.86604, 7.98608, 9.27646, 10.76126, 12.46768, 14.42646, 16.67225, 19.24413,
      22.18611,
    ],
    [
      1.19615, 1.42825, 1.70243, 2.02582, 2.40662, 2.85434, 3.37993, 3.99602, 4.71712, 5.55992,
      6.54355, 7.68997, 9.02427, 10.57517, 12.37545, 14.46251, 16.87895, 19.67325, 22.90052,
      26.62333,
    ],
    [
      1.20811, 1.45681, 1.75351, 2.10685, 2.52695, 3.0256, 3.61653, 4.3157, 5.14166, 6.11591,
      7.26334, 8.61276, 10.19742, 12.05569, 14.23177, 16.77652, 19.74838, 23.21444, 27.25162,
      31.948,
    ],
    [
      1.22019, 1.48595, 1.80611, 2.19112, 2.6533, 3.20714, 3.86968, 4.66096, 5.60441, 6.7275,
      8.06231, 9.64629, 11.52309, 13.74349, 16.36654, 19.46076, 23.1056, 27.39303, 32.42942,
      38.3376,
    ],
    [
      1.23239, 1.51567, 1.86029, 2.27877, 2.78596, 3.39956, 4.14056, 5.03383, 6.10881, 7.40025,
      8.94917, 10.80385, 13.02109, 15.66758, 18.82152, 22.57448, 27.03355, 32.32378, 38.59101,
      46.00512,
    ],
    [
      1.24472, 1.54598, 1.9161, 2.36992, 2.92526, 3.60354, 4.4304, 5.43654, 6.6586, 8.14027,
      9.93357, 12.10031, 14.71383, 17.86104, 21.64475, 26.1864, 31.62925, 38.14206, 45.92331,
      55.20614,
    ],
    [
      1.25716, 1.5769, 1.97359, 2.46472, 3.07152, 3.81975, 4.74053, 5.87146, 7.25787, 8.9543,
      11.02627, 13.55235, 16.62663, 20.36158, 24.89146, 30.37622, 37.00623, 45.00763, 54.64873,
      66.24737,
    ],
    [
      1.26973, 1.60844, 2.03279, 2.5633, 3.2251, 4.04893, 5.07237, 6.34118, 7.91108, 9.84973,
      12.23916, 15.17863, 18.78809, 23.21221, 28.62518, 35.23642, 43.29729, 53.10901, 65.03199,
      79.49685,
    ],
    [
      1.28243, 1.64061, 2.09378, 2.66584, 3.38635, 4.29187, 5.42743, 6.84848, 8.62308, 10.83471,
      13.58546, 17.00006, 21.23054, 26.46192, 32.91895, 40.87424, 50.65783, 62.66863, 77.38807,
      95.39622,
    ],
    [
      1.29526, 1.67342, 2.15659, 2.77247, 3.55567, 4.54938, 5.80735, 7.39635, 9.39916, 11.91818,
      15.07986, 19.04007, 23.99051, 30.16658, 37.8568, 47.41412, 59.26966, 73.94898, 92.09181,
      114.47546,
    ],
    [
      1.30821, 1.70689, 2.22129, 2.88337, 3.73346, 4.82235, 6.21387, 7.98806, 10.24508, 13.10999,
      16.73865, 21.32488, 27.10928, 34.38991, 43.53531, 55.00038, 69.3455, 87.2598, 109.58925,
      137.37055,
    ],
    [
      1.32129, 1.74102, 2.28793, 2.9987, 3.92013, 5.11169, 6.64884, 8.62711, 11.16714, 14.42099,
      18.5799, 23.88387, 30.63349, 39.20449, 50.06561, 63.80044, 81.13423, 102.96656, 130.41121,
      164.84466,
    ],
    [
      1.3345, 1.77584, 2.35657, 3.11865, 4.11614, 5.41839, 7.11426, 9.31727, 12.17218, 15.86309,
      20.62369, 26.74993, 34.61584, 44.69312, 57.57545, 74.00851, 94.92705, 121.50054, 155.18934,
      197.81359,
    ],
    [
      1.34785, 1.81136, 2.42726, 3.2434, 4.32194, 5.74349, 7.61226, 10.06266, 13.26768, 17.4494,
      22.8923, 29.95992, 39.1159, 50.95016, 66.21177, 85.84988, 111.06465, 143.37064, 184.67531,
      237.37631,
    ],
  ];

  const periods = Array.from({ length: 30 }, (_, i) => i + 1);
  const rates = Array.from({ length: 20 }, (_, i) => `${i + 1}%`);

  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-lg mb-4 text-center">W1의 미래가치</h3>
      <div className="overflow-auto max-h-[70vh]">
        <table className="w-full border-collapse text-[10px]">
          <thead>
            <tr>
              <th className="bg-blue-600 text-white p-1 border font-bold sticky left-0 text-[10px]">
                기간
              </th>
              {rates.map((rate, i) => (
                <th
                  key={i}
                  className={`p-1 border font-bold min-w-12 text-[10px] transition-colors ${
                    hoveredCell?.col === i
                      ? 'bg-yellow-300 text-black'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {rate}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {periods.map((period, rowIndex) => (
              <tr key={period}>
                <th
                  className={`p-1 border font-bold sticky left-0 text-[10px] transition-colors ${
                    hoveredCell?.row === rowIndex
                      ? 'bg-yellow-300 text-black'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {period}
                </th>
                {data[rowIndex]?.map((value, colIndex) => (
                  <td
                    key={colIndex}
                    className="text-center p-1 border hover:bg-blue-50 transition-colors text-[10px] cursor-pointer select-none"
                    onMouseEnter={() => setHoveredCell({ row: rowIndex, col: colIndex })}
                    onMouseLeave={() => setHoveredCell(null)}
                    onClick={() => copyToClipboard(value)}
                    title="클릭하여 복사"
                  >
                    {value.toFixed(5)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PresentValueTable = () => {
  const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null);
  const data = [
    [
      0.9901, 0.98039, 0.97087, 0.96154, 0.95238, 0.9434, 0.93458, 0.92593, 0.91743, 0.90909,
      0.9009, 0.89286, 0.88496, 0.87719, 0.86957, 0.86207, 0.8547, 0.84746, 0.84034, 0.83333,
    ],
    [
      0.9803, 0.96117, 0.9426, 0.92456, 0.90703, 0.89, 0.87344, 0.85734, 0.84168, 0.82645, 0.81162,
      0.79719, 0.78315, 0.76947, 0.75614, 0.74316, 0.73051, 0.71818, 0.70616, 0.69444,
    ],
    [
      0.97059, 0.94232, 0.91514, 0.889, 0.86384, 0.83962, 0.8163, 0.79383, 0.77218, 0.75131,
      0.73119, 0.71178, 0.69305, 0.67497, 0.65752, 0.64066, 0.62437, 0.60863, 0.59342, 0.5787,
    ],
    [
      0.96098, 0.92385, 0.88849, 0.8548, 0.8227, 0.79209, 0.7629, 0.73503, 0.70843, 0.68301,
      0.65873, 0.63552, 0.61332, 0.59208, 0.57175, 0.55229, 0.53365, 0.51579, 0.49867, 0.48225,
    ],
    [
      0.95147, 0.90573, 0.86261, 0.82193, 0.78353, 0.74726, 0.71299, 0.68058, 0.64993, 0.62092,
      0.59345, 0.56743, 0.54276, 0.51937, 0.49718, 0.47611, 0.45611, 0.43711, 0.41905, 0.40188,
    ],
    [
      0.94205, 0.88797, 0.83748, 0.79031, 0.74622, 0.70496, 0.66634, 0.63017, 0.59627, 0.56447,
      0.53464, 0.50663, 0.48032, 0.45559, 0.43233, 0.41044, 0.38984, 0.37043, 0.35214, 0.3349,
    ],
    [
      0.93272, 0.87056, 0.81309, 0.75992, 0.71068, 0.66506, 0.62275, 0.58349, 0.54703, 0.51316,
      0.48166, 0.45235, 0.42506, 0.39964, 0.37594, 0.35383, 0.3332, 0.31393, 0.29592, 0.27908,
    ],
    [
      0.92348, 0.85349, 0.78941, 0.73069, 0.67684, 0.62741, 0.58201, 0.54027, 0.50187, 0.46651,
      0.43393, 0.40388, 0.37616, 0.35056, 0.3269, 0.30503, 0.28478, 0.26604, 0.24867, 0.23257,
    ],
    [
      0.91434, 0.83676, 0.76642, 0.70259, 0.64461, 0.5919, 0.54393, 0.50025, 0.46043, 0.4241,
      0.39092, 0.36061, 0.33288, 0.30751, 0.28426, 0.26295, 0.2434, 0.22546, 0.20897, 0.19381,
    ],
    [
      0.90529, 0.82035, 0.74409, 0.67556, 0.61391, 0.55839, 0.50835, 0.46319, 0.42241, 0.38554,
      0.35218, 0.32197, 0.29459, 0.26974, 0.24718, 0.22668, 0.20804, 0.19106, 0.1756, 0.16151,
    ],
    [
      0.89632, 0.80426, 0.72242, 0.64958, 0.58468, 0.52679, 0.47509, 0.42888, 0.38753, 0.35049,
      0.31728, 0.28748, 0.2607, 0.23662, 0.21494, 0.19542, 0.17781, 0.16192, 0.14757, 0.13459,
    ],
    [
      0.88745, 0.78849, 0.70138, 0.6246, 0.55684, 0.49697, 0.44401, 0.39711, 0.35553, 0.31863,
      0.28584, 0.25668, 0.23071, 0.20756, 0.18691, 0.16846, 0.15197, 0.13722, 0.124, 0.11216,
    ],
    [
      0.87866, 0.77303, 0.68095, 0.60057, 0.53032, 0.46884, 0.41496, 0.3677, 0.32618, 0.28966,
      0.25751, 0.22917, 0.20416, 0.18207, 0.16253, 0.14523, 0.12989, 0.11629, 0.10421, 0.09346,
    ],
    [
      0.86996, 0.75788, 0.66112, 0.57748, 0.50507, 0.4423, 0.38782, 0.34046, 0.29925, 0.26333,
      0.23199, 0.20462, 0.18068, 0.15971, 0.14133, 0.1252, 0.11102, 0.09855, 0.08757, 0.07789,
    ],
    [
      0.86135, 0.74301, 0.64186, 0.55526, 0.48102, 0.41727, 0.36245, 0.31524, 0.27454, 0.23939,
      0.209, 0.1827, 0.15989, 0.1401, 0.12289, 0.10793, 0.09489, 0.08352, 0.07359, 0.06491,
    ],
    [
      0.85282, 0.72845, 0.62317, 0.53391, 0.45811, 0.39365, 0.33873, 0.29189, 0.25187, 0.21763,
      0.18829, 0.16312, 0.1415, 0.12289, 0.10686, 0.09304, 0.0811, 0.07078, 0.06184, 0.05409,
    ],
    [
      0.84438, 0.71416, 0.60502, 0.51337, 0.4363, 0.37136, 0.31657, 0.27027, 0.23107, 0.19784,
      0.16963, 0.14564, 0.12522, 0.1078, 0.09293, 0.08021, 0.06932, 0.05998, 0.05196, 0.04507,
    ],
    [
      0.83602, 0.70016, 0.58739, 0.49363, 0.41552, 0.35034, 0.29586, 0.25025, 0.21199, 0.17986,
      0.15282, 0.13004, 0.11081, 0.09456, 0.08081, 0.06914, 0.05925, 0.05083, 0.04367, 0.03756,
    ],
    [
      0.82774, 0.68643, 0.57029, 0.47464, 0.39573, 0.33051, 0.27651, 0.23171, 0.19449, 0.16351,
      0.13768, 0.11611, 0.09806, 0.08295, 0.07027, 0.05961, 0.05064, 0.04308, 0.0367, 0.0313,
    ],
    [
      0.81954, 0.67297, 0.55368, 0.45639, 0.37689, 0.3118, 0.25842, 0.21455, 0.17843, 0.14864,
      0.12403, 0.10367, 0.08678, 0.07276, 0.0611, 0.05139, 0.04328, 0.03651, 0.03084, 0.02608,
    ],
    [
      0.81143, 0.65978, 0.53755, 0.43883, 0.35894, 0.29416, 0.24151, 0.19866, 0.1637, 0.13513,
      0.11174, 0.09256, 0.0768, 0.06383, 0.05313, 0.0443, 0.03699, 0.03094, 0.02591, 0.02174,
    ],
    [
      0.8034, 0.64684, 0.52189, 0.42196, 0.34185, 0.27751, 0.22571, 0.18394, 0.15018, 0.12285,
      0.10067, 0.08264, 0.06796, 0.05599, 0.0462, 0.03819, 0.03162, 0.02622, 0.02178, 0.01811,
    ],
    [
      0.79544, 0.63416, 0.50669, 0.40573, 0.32557, 0.2618, 0.21095, 0.17032, 0.13778, 0.11168,
      0.09069, 0.07379, 0.06014, 0.04911, 0.04017, 0.03292, 0.02702, 0.02222, 0.0183, 0.01509,
    ],
    [
      0.78757, 0.62172, 0.49193, 0.39012, 0.31007, 0.24698, 0.19715, 0.1577, 0.1264, 0.10153,
      0.0817, 0.06588, 0.05323, 0.04308, 0.03493, 0.02838, 0.0231, 0.01883, 0.01538, 0.01258,
    ],
    [
      0.77977, 0.60953, 0.47761, 0.37512, 0.2953, 0.233, 0.18425, 0.14602, 0.11597, 0.0923, 0.07361,
      0.05882, 0.0471, 0.03779, 0.03038, 0.02447, 0.01974, 0.01596, 0.01292, 0.01048,
    ],
    [
      0.77205, 0.59758, 0.46369, 0.36069, 0.28124, 0.21981, 0.1722, 0.1352, 0.10639, 0.08391,
      0.06631, 0.05252, 0.04168, 0.03315, 0.02642, 0.02109, 0.01687, 0.01352, 0.01086, 0.00874,
    ],
    [
      0.7644, 0.58586, 0.45019, 0.34682, 0.26785, 0.20737, 0.16093, 0.12519, 0.09761, 0.07628,
      0.05974, 0.04689, 0.03689, 0.02908, 0.02297, 0.01818, 0.01442, 0.01146, 0.00912, 0.00728,
    ],
    [
      0.75684, 0.57437, 0.43708, 0.33348, 0.25509, 0.19563, 0.1504, 0.11591, 0.08955, 0.06934,
      0.05382, 0.04187, 0.03264, 0.02551, 0.01997, 0.01567, 0.01233, 0.00971, 0.00767, 0.00607,
    ],
    [
      0.74934, 0.56311, 0.42435, 0.32065, 0.24295, 0.18456, 0.14056, 0.10733, 0.08215, 0.06304,
      0.04849, 0.03738, 0.02889, 0.02237, 0.01737, 0.01351, 0.01053, 0.00823, 0.00644, 0.00506,
    ],
    [
      0.74192, 0.55207, 0.41199, 0.30832, 0.23138, 0.17411, 0.13137, 0.09938, 0.07537, 0.05731,
      0.04368, 0.03338, 0.02557, 0.01963, 0.0151, 0.01165, 0.009, 0.00697, 0.00541, 0.00421,
    ],
  ];

  const periods = Array.from({ length: 30 }, (_, i) => i + 1);
  const rates = Array.from({ length: 20 }, (_, i) => `${i + 1}%`);

  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-lg mb-4 text-center">W1의 현재가치</h3>
      <div className="overflow-auto max-h-[70vh]">
        <table className="w-full border-collapse text-[10px]">
          <thead>
            <tr>
              <th className="bg-blue-600 text-white p-1 border font-bold sticky left-0 text-[10px]">
                기간
              </th>
              {rates.map((rate, i) => (
                <th
                  key={i}
                  className={`p-1 border font-bold min-w-12 text-[10px] transition-colors ${
                    hoveredCell?.col === i
                      ? 'bg-yellow-300 text-black'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {rate}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {periods.map((period, rowIndex) => (
              <tr key={period}>
                <th
                  className={`p-1 border font-bold sticky left-0 text-[10px] transition-colors ${
                    hoveredCell?.row === rowIndex
                      ? 'bg-yellow-300 text-black'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {period}
                </th>
                {data[rowIndex]?.map((value, colIndex) => (
                  <td
                    key={colIndex}
                    className="text-center p-1 border hover:bg-blue-50 transition-colors text-[10px] cursor-pointer select-none"
                    onMouseEnter={() => setHoveredCell({ row: rowIndex, col: colIndex })}
                    onMouseLeave={() => setHoveredCell(null)}
                    onClick={() => copyToClipboard(value)}
                    title="클릭하여 복사"
                  >
                    {value.toFixed(5)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AnnuityFutureValueTable = () => {
  const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null);
  const data = [
    [
      1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0,
      1.0,
    ],
    [
      2.01, 2.02, 2.03, 2.04, 2.05, 2.06, 2.07, 2.08, 2.09, 2.1, 2.11, 2.12, 2.13, 2.14, 2.15, 2.16,
      2.17, 2.18, 2.19, 2.2,
    ],
    [
      3.0301, 3.0604, 3.0909, 3.1216, 3.1525, 3.1836, 3.2149, 3.2464, 3.2781, 3.31, 3.3421, 3.3744,
      3.4069, 3.4396, 3.4725, 3.5056, 3.5389, 3.5724, 3.6061, 3.64,
    ],
    [
      4.0604, 4.12161, 4.18363, 4.24646, 4.31013, 4.37462, 4.43994, 4.50611, 4.57313, 4.641,
      4.70973, 4.77933, 4.8498, 4.92114, 4.99338, 5.0665, 5.14051, 5.21543, 5.29126, 5.368,
    ],
    [
      5.10101, 5.20404, 5.30914, 5.41632, 5.52563, 5.63709, 5.75074, 5.8666, 5.98471, 6.1051,
      6.2278, 6.35285, 6.48027, 6.6101, 6.74238, 6.87714, 7.0144, 7.15421, 7.2966, 7.4416,
    ],
    [
      6.15202, 6.30812, 6.46841, 6.63298, 6.80191, 6.97532, 7.15329, 7.33593, 7.52333, 7.71561,
      7.91286, 8.11519, 8.32271, 8.53552, 8.75374, 8.97748, 9.20685, 9.44197, 9.68295, 9.92992,
    ],
    [
      7.21354, 7.43428, 7.66246, 7.89829, 8.14201, 8.39384, 8.65402, 8.9228, 9.20043, 9.48717,
      9.78327, 10.08901, 10.40466, 10.73049, 11.0668, 11.41387, 11.77201, 12.14152, 12.52271,
      12.9159,
    ],
    [
      8.28567, 8.58297, 8.89234, 9.21423, 9.54911, 9.89747, 10.2598, 10.63663, 11.02847, 11.43589,
      11.85943, 12.29969, 12.75726, 13.23276, 13.72682, 14.24009, 14.77325, 15.327, 15.90203,
      16.49908,
    ],
    [
      9.36853, 9.75463, 10.15911, 10.5828, 11.02656, 11.49132, 11.97799, 12.48756, 13.02104,
      13.57948, 14.16397, 14.77566, 15.41571, 16.08535, 16.78584, 17.51851, 18.28471, 19.08585,
      19.92341, 20.7989,
    ],
    [
      10.46221, 10.94972, 11.46388, 12.00611, 12.57789, 13.18079, 13.81645, 14.48656, 15.19293,
      15.93742, 16.72201, 17.54874, 18.41975, 19.3373, 20.30372, 21.32147, 22.39311, 23.52131,
      24.70886, 25.95868,
    ],
    [
      11.56683, 12.16872, 12.8078, 13.48635, 14.20679, 14.97164, 15.7836, 16.64549, 17.56029,
      18.53117, 19.56143, 20.65458, 21.81432, 23.04452, 24.34928, 25.7329, 27.19994, 28.75514,
      30.40355, 32.15042,
    ],
    [
      12.6825, 13.41209, 14.19203, 15.02581, 15.91713, 16.86994, 17.88845, 18.97713, 20.14072,
      21.38428, 22.71319, 24.13313, 25.65018, 27.27075, 29.00167, 30.85017, 32.82393, 34.93107,
      37.18022, 39.5805,
    ],
    [
      13.80933, 14.68033, 15.61779, 16.62684, 17.71298, 18.88214, 20.14064, 21.4953, 22.95338,
      24.52271, 26.21164, 28.02911, 29.9847, 32.08865, 34.35192, 36.7862, 39.40399, 42.21866,
      45.24446, 48.4966,
    ],
    [
      14.94742, 15.97394, 17.08632, 18.29191, 19.59863, 21.01507, 22.55049, 24.21492, 26.01919,
      27.97498, 30.09492, 32.3926, 34.88271, 37.58107, 40.50471, 43.67199, 47.10267, 50.81802,
      54.84091, 59.19592,
    ],
    [
      16.0969, 17.29342, 18.59891, 20.02359, 21.57856, 23.27597, 25.12902, 27.15211, 29.36092,
      31.77248, 34.40536, 37.27971, 40.41746, 43.84241, 47.58041, 51.65951, 56.11013, 60.96527,
      66.26068, 72.03511,
    ],
    [
      17.25786, 18.63929, 20.15688, 21.82453, 23.65749, 25.67253, 27.88805, 30.32428, 33.0034,
      35.94973, 39.18995, 42.75328, 46.67173, 50.98035, 55.71747, 60.92503, 66.64885, 72.93901,
      79.85021, 87.44213,
    ],
    [
      18.43044, 20.01207, 21.76159, 23.69751, 25.84037, 28.21288, 30.84022, 33.75023, 36.9737,
      40.5447, 44.50084, 48.88367, 53.73906, 59.1176, 65.07509, 71.67303, 78.97915, 87.06804,
      96.02175, 105.93056,
    ],
    [
      19.61475, 21.41231, 23.41444, 25.64541, 28.13238, 30.90565, 33.99903, 37.45024, 41.30134,
      45.59917, 50.39594, 55.74971, 61.72514, 68.39407, 75.83636, 84.14072, 93.40561, 103.74028,
      115.26588, 128.11667,
    ],
    [
      20.8109, 22.84056, 25.11687, 27.67123, 30.539, 33.75999, 37.37896, 41.44626, 46.01846,
      51.15909, 56.93949, 63.43968, 70.74941, 78.96923, 88.21181, 98.60323, 110.28456, 123.41353,
      138.1664, 154.74,
    ],
    [
      22.019, 24.29737, 26.87037, 29.77808, 33.06595, 36.78559, 40.99549, 45.76196, 51.16012,
      57.275, 64.20283, 72.05244, 80.94683, 91.02493, 102.44358, 115.37975, 130.03294, 146.62797,
      165.41802, 186.688,
    ],
    [
      23.23919, 25.78332, 28.67649, 31.9692, 35.71925, 39.99273, 44.86518, 50.42292, 56.76453,
      64.0025, 72.26514, 81.69874, 92.46992, 104.76842, 118.81012, 134.84051, 153.13854, 174.021,
      197.84744, 225.0256,
    ],
    [
      24.47159, 27.29898, 30.53678, 34.24797, 38.50521, 43.39229, 49.00574, 55.45676, 62.87334,
      71.40275, 81.21431, 92.50258, 105.49101, 120.436, 137.63164, 157.41499, 180.17209, 206.34479,
      236.43846, 271.03072,
    ],
    [
      25.7163, 28.84496, 32.45288, 36.61789, 41.43048, 46.99583, 53.43614, 60.8933, 69.53194,
      79.54302, 91.14788, 104.60289, 120.20484, 138.29704, 159.27638, 183.60138, 211.80134,
      244.48685, 282.36176, 326.23686,
    ],
    [
      26.97346, 30.42186, 34.42647, 39.0826, 44.502, 50.81558, 58.17667, 66.76476, 76.78981,
      88.49733, 102.17415, 118.15524, 136.83147, 158.65862, 184.16784, 213.97761, 248.80757,
      289.49448, 337.0105, 392.48424,
    ],
    [
      28.2432, 32.0303, 36.45926, 41.64591, 47.7271, 54.86451, 63.24904, 73.10594, 84.7009,
      98.34706, 114.41331, 133.33387, 155.61956, 181.87083, 212.79302, 249.21402, 292.10486,
      342.60349, 402.04249, 471.98108,
    ],
    [
      29.52563, 33.67091, 38.55304, 44.31174, 51.11345, 59.15638, 68.67647, 79.95442, 93.32398,
      109.18177, 127.99877, 150.33393, 176.8501, 208.33274, 245.71197, 290.08827, 342.76268,
      405.27211, 479.43056, 567.3773,
    ],
    [
      30.82089, 35.34432, 40.70963, 47.08421, 54.66913, 63.70577, 74.48382, 87.35077, 102.72313,
      121.09994, 143.07864, 169.37401, 200.84061, 238.49933, 283.56877, 337.50239, 402.03234,
      479.22109, 571.52237, 681.85276,
    ],
    [
      32.1291, 37.05121, 42.93092, 49.96758, 58.40258, 68.52811, 80.69769, 95.33883, 112.96822,
      134.20994, 159.81729, 190.69889, 227.94989, 272.88923, 327.10408, 392.50277, 471.37783,
      566.48089, 681.11162, 819.22331,
    ],
    [
      33.45039, 38.79223, 45.21885, 52.96629, 62.32271, 73.6398, 87.34653, 103.96594, 124.13536,
      148.63093, 178.39719, 214.58275, 258.58338, 312.09373, 377.16969, 456.30322, 552.51207,
      669.44745, 811.52283, 984.06797,
    ],
    [
      34.78489, 40.56808, 47.57542, 56.08494, 66.43885, 79.05819, 94.46079, 113.28321, 136.30754,
      164.49402, 199.02088, 241.33268, 293.19922, 356.78685, 434.74515, 530.31173, 647.43912,
      790.94799, 966.71217, 1181.88157,
    ],
  ];

  const periods = Array.from({ length: 30 }, (_, i) => i + 1);
  const rates = Array.from({ length: 20 }, (_, i) => `${i + 1}%`);

  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-lg mb-4 text-center">정상연금W1의 미래가치</h3>
      <div className="overflow-auto max-h-[70vh]">
        <table className="w-full border-collapse text-[10px]">
          <thead>
            <tr>
              <th className="bg-blue-600 text-white p-1 border font-bold sticky left-0 text-[10px]">
                기간
              </th>
              {rates.map((rate, i) => (
                <th
                  key={i}
                  className={`p-1 border font-bold min-w-12 text-[10px] transition-colors ${
                    hoveredCell?.col === i
                      ? 'bg-yellow-300 text-black'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {rate}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {periods.map((period, rowIndex) => (
              <tr key={period}>
                <th
                  className={`p-1 border font-bold sticky left-0 text-[10px] transition-colors ${
                    hoveredCell?.row === rowIndex
                      ? 'bg-yellow-300 text-black'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {period}
                </th>
                {data[rowIndex]?.map((value, colIndex) => (
                  <td
                    key={colIndex}
                    className="text-center p-1 border hover:bg-blue-50 transition-colors text-[10px] cursor-pointer select-none"
                    onMouseEnter={() => setHoveredCell({ row: rowIndex, col: colIndex })}
                    onMouseLeave={() => setHoveredCell(null)}
                    onClick={() => copyToClipboard(value)}
                    title="클릭하여 복사"
                  >
                    {value.toFixed(5)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AnnuityPresentValueTable = () => {
  const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null);
  const data = [
    [
      0.9901, 0.98039, 0.97087, 0.96154, 0.95238, 0.9434, 0.93458, 0.92593, 0.91743, 0.90909,
      0.9009, 0.89286, 0.88496, 0.87719, 0.86957, 0.86207, 0.8547, 0.84746, 0.84034, 0.83333,
    ],
    [
      1.9704, 1.94156, 1.91347, 1.88609, 1.85941, 1.83339, 1.80802, 1.78326, 1.75911, 1.73554,
      1.71252, 1.69005, 1.6681, 1.64666, 1.62571, 1.60523, 1.58521, 1.56564, 1.5465, 1.52778,
    ],
    [
      2.94099, 2.88388, 2.82861, 2.77509, 2.72325, 2.67301, 2.62432, 2.5771, 2.53129, 2.48685,
      2.44371, 2.40183, 2.36115, 2.32163, 2.28323, 2.24589, 2.20958, 2.17427, 2.13992, 2.10648,
    ],
    [
      3.90197, 3.80773, 3.7171, 3.6299, 3.54595, 3.46511, 3.38721, 3.31213, 3.23972, 3.16987,
      3.10245, 3.03735, 2.97447, 2.91371, 2.85498, 2.79818, 2.74324, 2.69006, 2.63859, 2.58873,
    ],
    [
      4.85343, 4.71346, 4.57971, 4.45182, 4.32948, 4.21236, 4.1002, 3.99271, 3.88965, 3.79079,
      3.6959, 3.60478, 3.51723, 3.43308, 3.35216, 3.27429, 3.19935, 3.12717, 3.05763, 2.99061,
    ],
    [
      5.79548, 5.60143, 5.41719, 5.24214, 5.07569, 4.91732, 4.76654, 4.62288, 4.48592, 4.35526,
      4.23054, 4.11141, 3.99755, 3.88867, 3.78448, 3.68474, 3.58918, 3.4976, 3.40978, 3.32551,
    ],
    [
      6.72819, 6.47199, 6.23028, 6.00205, 5.78637, 5.58238, 5.38929, 5.20637, 5.03295, 4.86842,
      4.7122, 4.56376, 4.42261, 4.2883, 4.16042, 4.03857, 3.92238, 3.81153, 3.7057, 3.60459,
    ],
    [
      7.65168, 7.32548, 7.01969, 6.73274, 6.46321, 6.20979, 5.9713, 5.74664, 5.53482, 5.33493,
      5.14612, 4.96764, 4.79877, 4.63886, 4.48732, 4.34359, 4.20716, 4.07757, 3.95437, 3.83716,
    ],
    [
      8.56602, 8.16224, 7.78611, 7.43533, 7.10782, 6.80169, 6.51523, 6.24689, 5.99525, 5.75902,
      5.53705, 5.32825, 5.13166, 4.94637, 4.77158, 4.60654, 4.45057, 4.30302, 4.16333, 4.03097,
    ],
    [
      9.4713, 8.98259, 8.5302, 8.1109, 7.72173, 7.36009, 7.02358, 6.71008, 6.41766, 6.14457,
      5.88923, 5.65022, 5.42624, 5.21612, 5.01877, 4.83323, 4.6586, 4.49409, 4.33893, 4.19247,
    ],
    [
      10.36763, 9.78685, 9.25262, 8.76048, 8.30641, 7.88687, 7.49867, 7.13896, 6.80519, 6.49506,
      6.20652, 5.9377, 5.68694, 5.45273, 5.23371, 5.02864, 4.83641, 4.65601, 4.4865, 4.32706,
    ],
    [
      11.25508, 10.57534, 9.954, 9.38507, 8.86325, 8.38384, 7.94269, 7.53608, 7.16073, 6.81369,
      6.49236, 6.19437, 5.91765, 5.66029, 5.42062, 5.19711, 4.98839, 4.79322, 4.6105, 4.43922,
    ],
    [
      12.13374, 11.34837, 10.63496, 9.98565, 9.39357, 8.85268, 8.35765, 7.90378, 7.4869, 7.10336,
      6.74987, 6.42355, 6.12181, 5.84236, 5.58315, 5.34233, 5.11828, 4.90951, 4.71471, 4.53268,
    ],
    [
      13.0037, 12.10625, 11.29607, 10.56312, 9.89864, 9.29498, 8.74547, 8.24424, 7.78615, 7.36669,
      6.98187, 6.62817, 6.30249, 6.00207, 5.72448, 5.46753, 5.2293, 5.00806, 4.80228, 4.61057,
    ],
    [
      13.86505, 12.84926, 11.93794, 11.11839, 10.37966, 9.71225, 9.10791, 8.55948, 8.06069, 7.60608,
      7.19087, 6.81086, 6.46238, 6.14217, 5.84737, 5.57546, 5.32419, 5.09158, 4.87586, 4.67547,
    ],
    [
      14.71787, 13.57771, 12.5611, 11.6523, 10.83777, 10.1059, 9.44665, 8.85137, 8.31256, 7.82371,
      7.37916, 6.97399, 6.60388, 6.26506, 5.95423, 5.6685, 5.40529, 5.16235, 4.9377, 4.72956,
    ],
    [
      15.56225, 14.29187, 13.16612, 12.16567, 11.27407, 10.47726, 9.76322, 9.12164, 8.54363,
      8.02155, 7.54879, 7.11963, 6.72909, 6.37286, 6.04716, 5.7487, 5.47461, 5.22233, 4.98966,
      4.77463,
    ],
    [
      16.39827, 14.99203, 13.75351, 12.6593, 11.68959, 10.8276, 10.05909, 9.37189, 8.75563, 8.20141,
      7.70162, 7.24967, 6.83991, 6.46742, 6.12797, 5.81785, 5.53385, 5.27316, 5.03333, 4.81219,
    ],
    [
      17.22601, 15.67846, 14.3238, 13.13394, 12.08532, 11.15812, 10.3356, 9.6036, 8.95011, 8.36492,
      7.83929, 7.36578, 6.93797, 6.55037, 6.19823, 5.87746, 5.58449, 5.31624, 5.07003, 4.8435,
    ],
    [
      18.04555, 16.35143, 14.87747, 13.59033, 12.46221, 11.46992, 10.59401, 9.81815, 9.12855,
      8.51356, 7.96333, 7.46944, 7.02475, 6.62313, 6.25933, 5.92884, 5.62777, 5.35275, 5.10086,
      4.86958,
    ],
    [
      18.85698, 17.01121, 15.41502, 14.02916, 12.82115, 11.76408, 10.83553, 10.0168, 9.29224,
      8.64869, 8.07507, 7.562, 7.10155, 6.68696, 6.31246, 5.97314, 5.66476, 5.38368, 5.12677,
      4.89132,
    ],
    [
      19.66038, 17.65805, 15.93692, 14.45112, 13.163, 12.04158, 11.06124, 10.20074, 9.44243,
      8.77154, 8.17574, 7.64465, 7.16951, 6.74294, 6.35866, 6.01133, 5.69637, 5.4099, 5.14855,
      4.90943,
    ],
    [
      20.45582, 18.2922, 16.44361, 14.85684, 13.48857, 12.30338, 11.27219, 10.37106, 9.58021,
      8.88322, 8.26643, 7.71843, 7.22966, 6.79206, 6.39884, 6.04425, 5.7234, 5.43212, 5.16685,
      4.92453,
    ],
    [
      21.24339, 18.91393, 16.93554, 15.24696, 13.79864, 12.55036, 11.46933, 10.52876, 9.70661,
      8.98474, 8.34814, 7.78432, 7.28288, 6.83514, 6.43377, 6.07263, 5.74649, 5.45095, 5.18223,
      4.9371,
    ],
    [
      22.02316, 19.52346, 17.41315, 15.62208, 14.09394, 12.78336, 11.65358, 10.67478, 9.82258,
      9.07704, 8.42174, 7.84314, 7.32998, 6.87293, 6.46415, 6.09709, 5.76623, 5.46691, 5.19515,
      4.94759,
    ],
    [
      22.7952, 20.12104, 17.87684, 15.98277, 14.37519, 13.00317, 11.82578, 10.80998, 9.92897,
      9.16095, 8.48806, 7.89566, 7.37167, 6.90608, 6.49056, 6.11818, 5.78311, 5.48043, 5.20601,
      4.95632,
    ],
    [
      23.55961, 20.7069, 18.32703, 16.32959, 14.64303, 13.21053, 11.98671, 10.93516, 10.02658,
      9.23722, 8.5478, 7.94255, 7.40856, 6.93515, 6.51353, 6.13636, 5.79753, 5.49189, 5.21513,
      4.9636,
    ],
    [
      24.31644, 21.28127, 18.76411, 16.66306, 14.89813, 13.40616, 12.13711, 11.05108, 10.11613,
      9.30657, 8.60162, 7.98442, 7.4412, 6.96066, 6.53351, 6.15204, 5.80985, 5.5016, 5.2228,
      4.96967,
    ],
    [
      25.06579, 21.84438, 19.18845, 16.98371, 15.14107, 13.59072, 12.27767, 11.15841, 10.19828,
      9.36961, 8.65011, 8.02181, 7.47009, 6.98304, 6.55088, 6.16555, 5.82039, 5.50983, 5.22924,
      4.97472,
    ],
    [
      25.80771, 22.39646, 19.60044, 17.29203, 15.37245, 13.76483, 12.40904, 11.25778, 10.27365,
      9.42691, 8.69379, 8.05518, 7.49565, 7.00266, 6.56598, 6.1772, 5.82939, 5.51681, 5.23466,
      4.97894,
    ],
  ];

  const periods = Array.from({ length: 30 }, (_, i) => i + 1);
  const rates = Array.from({ length: 20 }, (_, i) => `${i + 1}%`);

  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-lg mb-4 text-center">정상연금W1의 현재가치</h3>
      <div className="overflow-auto max-h-[70vh]">
        <table className="w-full border-collapse text-[10px]">
          <thead>
            <tr>
              <th className="bg-blue-600 text-white p-1 border font-bold sticky left-0 text-[10px]">
                기간
              </th>
              {rates.map((rate, i) => (
                <th
                  key={i}
                  className={`p-1 border font-bold min-w-12 text-[10px] transition-colors ${
                    hoveredCell?.col === i
                      ? 'bg-yellow-300 text-black'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {rate}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {periods.map((period, rowIndex) => (
              <tr key={period}>
                <th
                  className={`p-1 border font-bold sticky left-0 text-[10px] transition-colors ${
                    hoveredCell?.row === rowIndex
                      ? 'bg-yellow-300 text-black'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {period}
                </th>
                {data[rowIndex]?.map((value, colIndex) => (
                  <td
                    key={colIndex}
                    className="text-center p-1 border hover:bg-blue-50 transition-colors text-[10px] cursor-pointer select-none"
                    onMouseEnter={() => setHoveredCell({ row: rowIndex, col: colIndex })}
                    onMouseLeave={() => setHoveredCell(null)}
                    onClick={() => copyToClipboard(value)}
                    title="클릭하여 복사"
                  >
                    {value.toFixed(5)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FactorTablesModal;
